import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAssessment } from '../../context/AssessmentContext';
import { useUser } from '../../context/UserContext';
import ParticleEffect from '../ui/ParticleEffect';
import AnimatedBackground from '../ui/AnimatedBackground';
import { Flower2Icon, LeafIcon, MailIcon, PhoneIcon, UserIcon } from 'lucide-react';
const CommunityOptIn: React.FC = () => {
  const navigate = useNavigate();
  const {
    assessmentData,
    updateAssessmentData,
    setIsComplete
  } = useAssessment();
  const {
    updateUserData
  } = useUser();
  const [email, setEmail] = useState(assessmentData.email || '');
  const [phone, setPhone] = useState(assessmentData.phone || '');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    email?: string;
    name?: string;
  }>({});
  const validateForm = () => {
    const newErrors: {
      email?: string;
      name?: string;
    } = {};
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!name) {
      newErrors.name = 'Name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setIsSubmitting(true);
    // Update assessment data with contact info
    updateAssessmentData({
      email,
      phone,
      timestamp: new Date()
    });
    // In a real app, you'd submit the data to a server here
    // Simulate API call with timeout
    setTimeout(() => {
      // Update user data with assessment results
      updateUserData({
        name,
        email,
        currentGarden: mapHealingToGarden(assessmentData.primaryHealing),
        gardensRestored: 0,
        completedQuests: []
      });
      // Mark assessment as complete
      setIsComplete(true);
      setIsSubmitting(false);
      // Navigate to onboarding
      navigate('/onboarding');
    }, 1500);
  };
  // Map healing intent to garden value
  const mapHealingToGarden = (healing: string): string => {
    const gardenMap: Record<string, string> = {
      phoenix: 'Bravery',
      dove: 'Love',
      wolf: 'Respect',
      butterfly: 'Humility',
      eagle: 'Wisdom',
      owl: 'Truth',
      dragon: 'Honesty'
    };
    return gardenMap[healing] || 'Wisdom';
  };
  return <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative" data-id="element-503">
      <AnimatedBackground type="cosmic" data-id="element-504" />
      <ParticleEffect count={50} data-id="element-505" />
      {/* Decorative elements */}
      <div className="absolute top-10 left-10 garden-leaf opacity-20" data-id="element-506">
        <LeafIcon size={80} className="text-green-200" data-id="element-507" />
      </div>
      <div className="absolute bottom-20 right-20 garden-leaf opacity-20" data-id="element-508">
        <LeafIcon size={100} className="text-green-200" data-id="element-509" />
      </div>
      <div className="absolute top-1/4 right-1/4 garden-flower opacity-20" data-id="element-510">
        <Flower2Icon size={60} className="text-pink-200" data-id="element-511" />
      </div>
      <div className="w-full max-w-4xl" data-id="element-512">
        <h1 className="text-4xl md:text-5xl font-bold text-center text-white mb-6" data-id="element-513">
          <span className="text-pink-300" data-id="element-514">Seven</span>After
        </h1>
        <div className="bg-white bg-opacity-10 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-white border-opacity-20 shadow-xl relative overflow-hidden" data-id="element-515">
          <div className="spiral-pattern absolute inset-0 opacity-20 z-0" data-id="element-516"></div>
          <div className="lotus-pattern absolute inset-0 opacity-20 z-0" data-id="element-517"></div>
          <h2 className="text-2xl md:text-3xl font-bold text-center text-white mb-4 relative z-10" data-id="element-518">
            Create Your Sacred Garden Account
          </h2>
          <p className="text-white text-center mb-6 relative z-10" data-id="element-519">
            Your personalized healing journey awaits. Enter your details to
            begin restoring your seven sacred gardens.
          </p>
          <form onSubmit={handleSubmit} className="space-y-4 relative z-10" data-id="element-520">
            <div data-id="element-521">
              <label htmlFor="name" className="block text-white mb-2 font-medium" data-id="element-522">
                Your Name
              </label>
              <div className="relative" data-id="element-523">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-524">
                  <UserIcon size={18} className="text-pink-300" data-id="element-525" />
                </div>
                <input type="text" id="name" value={name} onChange={e => setName(e.target.value)} className={`w-full pl-10 pr-4 py-3 rounded-lg bg-white bg-opacity-20 text-white border ${errors.name ? 'border-red-400' : 'border-white border-opacity-30'} focus:outline-none focus:ring-2 focus:ring-pink-400 placeholder-white placeholder-opacity-50`} placeholder="Your full name" data-id="element-526" />
              </div>
              {errors.name && <p className="mt-1 text-red-300 text-sm" data-id="element-527">{errors.name}</p>}
            </div>
            <div data-id="element-528">
              <label htmlFor="email" className="block text-white mb-2 font-medium" data-id="element-529">
                Email Address
              </label>
              <div className="relative" data-id="element-530">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-531">
                  <MailIcon size={18} className="text-pink-300" data-id="element-532" />
                </div>
                <input type="email" id="email" value={email} onChange={e => setEmail(e.target.value)} className={`w-full pl-10 pr-4 py-3 rounded-lg bg-white bg-opacity-20 text-white border ${errors.email ? 'border-red-400' : 'border-white border-opacity-30'} focus:outline-none focus:ring-2 focus:ring-pink-400 placeholder-white placeholder-opacity-50`} placeholder="Your email address" data-id="element-533" />
              </div>
              {errors.email && <p className="mt-1 text-red-300 text-sm" data-id="element-534">{errors.email}</p>}
            </div>
            <div data-id="element-535">
              <label htmlFor="phone" className="block text-white mb-2 font-medium" data-id="element-536">
                Phone Number (Optional)
              </label>
              <div className="relative" data-id="element-537">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-538">
                  <PhoneIcon size={18} className="text-pink-300" data-id="element-539" />
                </div>
                <input type="tel" id="phone" value={phone} onChange={e => setPhone(e.target.value)} className="w-full pl-10 pr-4 py-3 rounded-lg bg-white bg-opacity-20 text-white border border-white border-opacity-30 focus:outline-none focus:ring-2 focus:ring-pink-400 placeholder-white placeholder-opacity-50" placeholder="Your phone number (optional)" data-id="element-540" />
              </div>
            </div>
            <div className="pt-4" data-id="element-541">
              <button type="submit" disabled={isSubmitting} className="w-full py-4 rounded-xl transition-all duration-300 text-white font-semibold bg-gradient-to-r from-pink-500 to-orange-500 hover:shadow-lg cursor-pointer mystical-button glow flex items-center justify-center" data-id="element-542">
                {isSubmitting ? <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" data-id="element-543"></div>
                    Creating Your Garden...
                  </> : 'Begin Your Sacred Journey'}
              </button>
            </div>
            <p className="text-center text-white text-opacity-70 text-sm" data-id="element-544">
              By continuing, you agree to our Terms of Service and Privacy
              Policy. We'll send you personalized guidance based on your
              assessment.
            </p>
          </form>
        </div>
      </div>
    </div>;
};
export default CommunityOptIn;